import {useConfig} from '@aboutyou/config';
import {TransactionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeatureInViewImpression,
  useTrackPurchase,
} from '@aboutyou/tracking';
import {HeightTransition} from '@aboutyou/transitions';
import {
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  MessageBox,
  SPACING,
  Text,
  UnstyledButton,
  desktopAndUpLinaria,
  extraLargeScreensLinaria,
  higherDesktopAndUpLinaria,
  largeScreensLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect, useState} from 'react';
interface OrderSummaryInfo {
  label: string;
  value: string;
}

interface OrderSummaryProps {
  headline: string;
  subheadline: string;
  infos: OrderSummaryInfo[];
  transactionTracker: TransactionTracker;
  className?: string;
  isResponsive: boolean;
}

const useTrackSuccess = (tracker: TransactionTracker) => {
  const trackPurchase = useTrackPurchase();

  useEffect(() => {
    trackPurchase(tracker);
  }, []);
};

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  headline,
  subheadline,
  infos,
  transactionTracker,
  className,
  isResponsive,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {device} = useConfig();

  useTrackSuccess(transactionTracker);

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'order_summary',
    name: 'order_summary',
  });

  return (
    <OrderSummaryStreamWrapper
      ref={ref}
      className={className}
      data-is-responsive={isResponsive}
    >
      <ClickContainer
        device={device}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      >
        <StyledMessageBox
          data-testid="ospSummaryContainer"
          icon={GenericIconName.CheckmarkBorder}
          backgroundColor="Green300"
          isResponsive={isResponsive}
        >
          <HeadlineContainer>
            <Headline weight="mediumHeavy">{headline}</Headline>
            <Subheadline weight="mediumBold">{subheadline}</Subheadline>
          </HeadlineContainer>

          <AnimationContainer isExpanded={isExpanded} device={device}>
            <InfoContainer>
              {infos.map(info => (
                <Info key={info.label} data-testid="orderSummaryInfo">
                  <Value weight="mediumHeavy">{info.value}</Value>
                  <Label weight="mediumBold">{info.label}</Label>
                </Info>
              ))}
            </InfoContainer>
          </AnimationContainer>

          {device === 'mobile' && (
            <Icon
              asset={
                isExpanded ? GenericIconName.ArrowUp : GenericIconName.ArrowDown
              }
              size="XS"
            />
          )}
        </StyledMessageBox>
      </ClickContainer>
    </OrderSummaryStreamWrapper>
  );
};

const ClickContainer: React.FC<
  React.PropsWithChildren<{
    isExpanded: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    device: 'mobile' | 'desktop';
  }>
> = ({children, isExpanded, setIsExpanded, device}) => {
  const {t} = useTranslation();

  return device === 'mobile' ? (
    <UnstyledButton
      aria-label={
        isExpanded ? t('Collapse order summary') : t('Expand order summary')
      }
      onClick={() => setIsExpanded(!isExpanded)}
      style={{width: '100%'}}
    >
      {children}
    </UnstyledButton>
  ) : (
    <>{children}</>
  );
};

const AnimationContainer: React.FC<
  React.PropsWithChildren<{device: 'mobile' | 'desktop'; isExpanded: boolean}>
> = ({children, device, isExpanded}) => {
  return device === 'mobile' ? (
    <StyledHeightTransition mounted={isExpanded} duration={300}>
      {children}
    </StyledHeightTransition>
  ) : (
    <>{children}</>
  );
};

const Value = styled(Text)`
  font-size: ${FONT_SIZES.px16};
  line-height: 1.5;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 1.2;
  `)}
`;

const Label = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  line-height: 1.5;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
    line-height: 1.4;
  `)}
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px20};
  line-height: 1.2;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px32};
    line-height: 1.25;
  `)}
`;

const Subheadline = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  line-height: 1.5;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
    line-height: 1.4;
  `)}
`;

const HeadlineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Xs']};
  margin-bottom: ${SPACING['Y.Md']};

  ${desktopAndUpLinaria(`
    flex: 1;
    padding: 0;
    margin-bottom: ${SPACING['Y.Lg']};
  `)}

  [data-is-responsive='true'] & {
    ${tabletAndUpLinaria(`
      margin-bottom: 0;
    `)}
  }
`;

export const OrderSummaryStreamWrapper = styled.div`
  background-color: ${COLORS.White};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};

  ${tabletAndUpLinaria(`
    padding: ${SPACING['Y.Lg']} 0;
  `)}

  [data-is-responsive='true'] & {
    ${tabletAndUpLinaria(`
      padding: ${SPACING['Y.Xl2']} ${SPACING['X.Xl4']} ${SPACING['Y.Xl2']} ${SPACING['X.Xl5']};
    `)}
  }
`;

const StyledMessageBox = styled(MessageBox)`
  gap: 0;
  padding: ${SPACING['Y.Xl2']} ${SPACING['X.Xl']} ${SPACING['Y.Sm']};

  ${tabletAndUpLinaria(`
    height: calc(100% - ${SPACING['Y.Xl']});
    padding: ${SPACING['Y.Xl2']} ${SPACING['X.Xl']};
  `)}

  [data-is-responsive='true'] & {
    ${tabletAndUpLinaria(`
      gap: ${SPACING['X.Xl']}
    `)}

    ${desktopAndUpLinaria(`
      height: 100%;
    `)}
  }
`;

const StyledHeightTransition = styled(HeightTransition)`
  width: 100%;

  [data-is-responsive='true'] & {
    ${tabletAndUpLinaria(`
      flex: 1;
    `)}
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: ${SPACING['Y.Md']};
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  [data-is-responsive='false'] & {
    ${desktopAndUpLinaria(`
      margin-bottom: 0;
    `)}

    ${higherDesktopAndUpLinaria(`
      max-width: 90%;
    `)}

    ${largeScreensLinaria(`
      max-width: 70%;
    `)}


    ${extraLargeScreensLinaria(`
      max-width: 60%;
    `)}
  }

  [data-is-responsive='true'] & {
    width: 100%;
    flex: 1;

    ${desktopAndUpLinaria(`
      height: 100%;
      border-left: 1px solid ${COLORS.Black};
    `)}
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  [data-is-responsive='true'] & {
    ${tabletAndUpLinaria(`
      flex: 1;
    `)}
  }
`;
